import { ActionIcon, Box, Button, Badge, Flex, Tooltip } from '@mantine/core'
import { ColumnDef } from '@tanstack/react-table'
import { Tradeshow } from 'api/dto/tradeshow'
import { useTradeshows } from 'api/query/tradeshow'
import { Loading } from 'components/loading/loading'
import { Table } from 'components/table'
import { useTranslation } from 'react-i18next'
import { IconEdit, IconExternalLink, IconHomeLink, IconPlus, IconShare, IconMail, IconDownload } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { AddTradeshow } from 'components/modal/tradeshow/add.tradeshow'
import { useState } from 'react'
import { EditTradeshow } from 'components/modal/tradeshow/edit.tradeshow'
import { DateTime, DateTimeFormat } from 'utils/date-time'
import { showNotification } from '@mantine/notifications'
import { useGlobalPrices, useTaxRates } from 'api/query/price'
import { useKiosks } from 'api/query/kiosk'
import { PreRegisterEmailModal } from 'components/modal/pre-register/pre-register-email.modal'
import { exportActiveEventPricesToCsv } from 'utils/export'
import { isTradeshowEnded } from 'utils/tradeshow'
import { EndedTooltip } from 'components/tradeshow/ended-tooltip'

export function Tradeshows() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: tradeshows, isLoading } = useTradeshows()
  const { data: kiosks } = useKiosks()
  const { data: taxRates } = useTaxRates()
  const { data: globalPrices } = useGlobalPrices()
  const [selectedTradeshow, setSelectedTradeshow] = useState<Tradeshow | undefined>()
  const [addTradeshowModalOpened, setAddTradeshowModalOpened] = useState(false)
  const [preRegisterModalOpened, setPreRegisterModalOpened] = useState(false)
  const [preRegisterTradeshow, setPreRegisterTradeshow] = useState<Tradeshow | undefined>()
  const [isExporting, setIsExporting] = useState(false)

  const handleExportActiveEventPrices = async () => {
    if (!tradeshows || !globalPrices) return
    setIsExporting(true)
    try {
      await exportActiveEventPricesToCsv(tradeshows, globalPrices)
    } catch {
      showNotification({
        message: t('common.error.message'),
        color: 'red',
        title: t('common.error.title'),
      })
    } finally {
      setIsExporting(false)
    }
  }

  const columns: ColumnDef<Tradeshow>[] = [
    {
      id: 'name',
      header: t('tradeshow.name'),
      accessorKey: 'name',
      cell: (props) => (
        <Flex align="center" gap={8}>
          <Box>{props.getValue() as string}</Box>
          {isTradeshowEnded(props.row.original) && (
            <Badge color="gray" variant="filled" size="xs">
              {t('tradeshow.ended')}
            </Badge>
          )}
        </Flex>
      ),
    },
    {
      // Hidden: only used to keep ended shows below the others in the default sort
      id: 'ended',
      accessorFn: (tradeshow) => (isTradeshowEnded(tradeshow) ? 1 : 0),
      enableGlobalFilter: false,
    },
    {
      id: 'kiosk',
      header: t('tradeshow.kiosk'),
      cell: (props) => {
        const kioskList = kiosks?.filter((kiosk) => kiosk.tradeShowId === props.row.original.id) || []
        return (
          <Box>
            {kioskList.length > 0 ? (
              <Flex gap={5} wrap="wrap">
                {kioskList.map((kiosk) => (
                  <Badge key={kiosk.id} color="custom-primary-color.0" variant="light" c="#fff">
                    {t('tradeshow.kiosk')} {kiosk.id}
                  </Badge>
                ))}
              </Flex>
            ) : null}
          </Box>
        )
      },
    },
    {
      id: 'description',
      header: t('tradeshow.description'),
      accessorKey: 'description',
      maxSize: 200,
    },
    {
      id: 'startDate',
      accessorKey: 'startDate',
      header: t('tradeshow.start-date'),
      cell: (props) => <Box>{DateTime.fromDate(props.getValue() as Date).toFormat(DateTimeFormat.YearMonthAndDay)}</Box>,
    },
    {
      id: 'endDate',
      accessorKey: 'endDate',
      header: t('tradeshow.end-date'),
      cell: (props) => <Box>{DateTime.fromDate(props.getValue() as Date).toFormat(DateTimeFormat.YearMonthAndDay)}</Box>,
    },
    {
      id: 'tradeshow-tax',
      accessorKey: 'config.taxRateId',
      header: t('tradeshow.config.taxRate'),
      cell: (props) => {
        const tax = taxRates?.find((taxRate) => taxRate.id === props.getValue())
        return tax ? <Box>{`${tax.name} - ${tax.state} ${tax.percentage}%`}</Box> : null
      },
    },
    {
      id: 'actions',
      header: t('tradeshow.actions'),
      cell: (props) => (
        <Box display="flex">
          <ActionIcon
            mr={5}
            color="custom-primary-color.0"
            variant="outline"
            size="md"
            onClick={() => {
              showNotification({
                message: t('common.url-copied'),
                color: 'green',
                title: t('common.copied'),
                autoClose: 3000,
              })

              void navigator.clipboard.writeText(`${window.location.host}/#/tradeshows/${props.row.original.id}/register`)
            }}
          >
            <IconShare width="20px" />
          </ActionIcon>
          <Box mr={5}>
            <EndedTooltip ended={isTradeshowEnded(props.row.original)}>
              <ActionIcon
                color="green"
                variant="outline"
                size="md"
                disabled={isTradeshowEnded(props.row.original)}
                onClick={() => {
                  setPreRegisterTradeshow(props.row.original)
                  setPreRegisterModalOpened(true)
                }}
              >
                <IconMail size="20px" />
              </ActionIcon>
            </EndedTooltip>
          </Box>
          <ActionIcon mr={5} variant="outline" size="md" onClick={() => setSelectedTradeshow(props.row.original)}>
            <IconEdit size="20px" />
          </ActionIcon>
          <ActionIcon color="blue" variant="outline" size="md" onClick={() => navigate(`/tradeshows/${props.row.original.id}`)}>
            <IconExternalLink size="20px" />
          </ActionIcon>
        </Box>
      ),
    },
  ]

  if (isLoading) {
    return <Loading height="calc(100vh - 235px)" size="md" />
  }

  return (
    <>
      <Table<Tradeshow>
        data={tradeshows || []}
        columns={columns}
        hiddenColumns={{ ended: false }}
        defaultSort={[
          { id: 'ended', desc: false },
          { id: 'startDate', desc: true },
        ]}
        renderRow={(row, children) => (
          // Ended shows: dim the data cells only, the last cell (actions) keeps its normal look
          <Box component="tr" key={row.id} sx={isTradeshowEnded(row.original) ? { '& > td:not(:last-of-type)': { opacity: 0.55, filter: 'grayscale(1)' } } : undefined}>
            {children}
          </Box>
        )}
        actionButtons={() => [
          <Tooltip label={t('tradeshow.export-active-prices')} withArrow position="bottom">
            <Button h={44} px="sm" color="green" variant="outline" mr={10} onClick={() => void handleExportActiveEventPrices()} loading={isExporting}>
              <IconDownload size={20} />
            </Button>
          </Tooltip>,
          <Button h={44} size="sm" leftIcon={<IconPlus size={20} />} color="custom-primary-color.0" onClick={() => setAddTradeshowModalOpened(true)}>
            {t('tradeshow.create')}
          </Button>,
        ]}
      />
      <AddTradeshow opened={addTradeshowModalOpened} onClose={() => setAddTradeshowModalOpened(false)} />
      <EditTradeshow opened={!!selectedTradeshow} tradeshow={selectedTradeshow} onClose={() => setSelectedTradeshow(undefined)} />
      {preRegisterTradeshow && (
        <PreRegisterEmailModal
          opened={preRegisterModalOpened}
          onClose={() => {
            setPreRegisterModalOpened(false)
            setPreRegisterTradeshow(undefined)
          }}
          targetTradeShowId={preRegisterTradeshow.id}
          targetTradeShowName={preRegisterTradeshow.name}
        />
      )}
    </>
  )
}
